import { Button } from '@calabriocloud/beacon-ui'
import { UserAssignmentRow } from './UserAssignmentRow'
import { useTranslation } from 'react-i18next'
import { ReactElement, useState, useCallback, useEffect } from 'react'
import { useQuery } from 'react-query'
import axios from 'axios'
import { Assignee, Goal, Who } from '../../../constants'
import { useFormikContext } from 'formik'

const viewGoalUsersEndpoint = '/api/rest/org/common/person/permission/ViewContactGoal?includeUpwardScope=true'

export interface AssigneeMappingField {
  rowId: number,
  assignee: Assignee,
  whoIds: Who[],
}

export interface ViewGoalUser {
  id: number,
  personId: number,
  firstName: string,
  lastName: string,
  email: string,
  isSynchronized: boolean,
  isReconcileOnly: boolean,
  displayId: string,
  label: string,
  isSystemUser: boolean,
}

function reducer(accumlator: { [key: string]: number[] }, current: AssigneeMappingField) {
  if (current.whoIds.length === 0 && current.assignee) {
    return accumlator
  }

  const whoIds = current.whoIds.map(who => who.id)
  accumlator[current.assignee.id.toString()] = whoIds
  return accumlator
}

interface UserAssignmentProps {
  id: string,
  name: string,
}

export const UserAssignment = ({
  name,
}: UserAssignmentProps): ReactElement => {
  const { t } = useTranslation()
  const formik = useFormikContext<Goal>()
  const { assigneeToWhoIds, whoType } = formik.values
  const { isDisabled } = formik.status
  const [curWhoType, setCurWhoType] = useState(whoType)
  const { data, isLoading } = useQuery<Assignee[]>({
    queryKey: ['viewGoalUsers'],
    queryFn: async () => {
      const res = await axios.get<Omit<ViewGoalUser, 'id' | 'label'>[]>(viewGoalUsersEndpoint)
      return res.data.map(item => ({ id: item.personId.toString(), name: item.displayId, ...item }))
    },
  })
  const [mappingId, setMappingId] = useState<number>(1)
  const [mappings, setMappings] = useState<AssigneeMappingField[]>([{
    rowId: 1,
    assignee: { id: '', name: '' },
    whoIds: [],
  }])
  const selectedAssigneeIds = mappings.map(mapping => mapping.assignee.id).filter(id => id !== '')

  // Update mappings once data is loaded
  useEffect(() => {
    if (isLoading || !data) return

    // Only try to initialize from assigneeToWhoIds if user hasn't made edits yet
    if (assigneeToWhoIds && Object.keys(assigneeToWhoIds).length > 0) {
      const availableUserIds = data.map(user => user.id)
      const idsToValidate = Object.keys(assigneeToWhoIds)
      const hasInvalidAssigneesOrWhoIds = idsToValidate.some(id => !availableUserIds.includes(id.toString()))

      if (hasInvalidAssigneesOrWhoIds) {
        // Invalid assignees found - reset to default state
        resetMappings()
      } else {
        // Valid assignees - set mappings from assigneeToWhoIds
        const validMappings = Object.entries(assigneeToWhoIds).map(([key, value], index) => ({
          rowId: index + 1,
          assignee: { id: key, name: '' },
          whoIds: (value || []).map(whoId => ({ id: whoId, textValue: '' })),
        }))

        setMappings(validMappings)
        setMappingId(validMappings.length)
      }
    }
  }, [data, isLoading])

  const handleAddMapping = () => {
    setMappings(prev => {
      return [
        ...prev,
        {
          rowId: mappingId + 1,
          assignee: { id: '', name: '' },
          whoIds: [],
        },
      ]
    })
    setMappingId(prev => prev + 1)
  }

  const handleOnChange = (mapping: AssigneeMappingField) => {
    setMappings(prevMapping => {
      const newMappings = prevMapping.map(item => {
        if (item.rowId === mapping.rowId) {
          return mapping
        }
        return item
      })

      formik.setFieldValue('assigneeToWhoIds', newMappings.reduce(reducer, {}))
      return newMappings
    })
  }

  const handleOnRemoveRow = (id: number) => {
    setMappings(prevMappings => {
      const newMappings = prevMappings.filter(mapping => mapping.rowId !== id)

      formik.setFieldValue('assigneeToWhoIds', newMappings.reduce(reducer, {}))
      return Object.keys(newMappings).length > 0
        ? newMappings
        : [{
          rowId: 1,
          assignee: { id: '', name: '' },
          whoIds: [],
        }]
    })
  }
  // manully trigger validation on blur
  const handleOnBlur = useCallback((_e: React.FocusEvent<Element>) => {
    formik.setFieldTouched(name)
    formik.validateField(name)
  }, [assigneeToWhoIds, name])

  const resetMappings = () => {
    setMappings(() => [{
      rowId: 1,
      assignee: { id: '', name: '' },
      whoIds: [],
    }])
    setMappingId(1)
    setCurWhoType(whoType)
    formik.setFieldValue('assigneeToWhoIds', {})
  }
  // Reset mappings when whoType changes
  useEffect(() => {
    if (curWhoType === whoType) {
      return
    }
    resetMappings()
  }, [whoType])

  return (
    <div className='py-4'>
      <h4 className="mb-1 text-xl">{t('users')}</h4>
      <p className="text-sm mb-4">{t('goalUserDescription')}</p>
      {!isLoading && mappings.map((mapping, index) => (
        <UserAssignmentRow
          key={mapping.rowId}
          mapping={mapping}
          assignees={data?.filter(({ id }) => id === mapping.assignee.id || !selectedAssigneeIds.includes(id)) || []}
          index={index}
          onChange={handleOnChange}
          onRemoveRow={handleOnRemoveRow}
          onBlur={handleOnBlur}
        />
      ))}
      {!isDisabled && !!mappings?.[0]?.whoIds.length && <Button variant='primary' data-testid="user-assigment-add-button" onPress={handleAddMapping}>{t('add')}</Button>}
    </div>
  )
}
